import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AssessmentTreeShape, type QuestionShape } from '@/lib/api';
import type { QuestionType } from '@eduborg/shared';

export const assessmentByItemKey = (itemId: string) => ['assessment-by-item', itemId] as const;
export const assessmentTreeKey = (id: string) => ['assessment-tree', id] as const;

export function useAssessmentByItemQuery(token: string, itemId: string | null) {
  return useQuery({
    queryKey: assessmentByItemKey(itemId ?? 'none'),
    queryFn: () => api.assessments.getByItemId(token, itemId!),
    enabled: token.length > 0 && itemId !== null,
    staleTime: 30_000,
  });
}

export function useAssessmentTreeQuery(token: string, id: string | null) {
  return useQuery({
    queryKey: assessmentTreeKey(id ?? 'none'),
    queryFn: () => api.assessments.getTree(token, id!),
    enabled: token.length > 0 && id !== null,
    staleTime: 5_000,
  });
}

export function useAddQuestionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: QuestionType;
      promptHtml: string;
      points?: number;
      payloadJson?: string;
    }) => api.assessments.addQuestion(token, assessmentId, body),
    onSuccess: ({ question }) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return { ...prev, questions: [...prev.questions, { ...question, options: [] }] };
      });
    },
  });
}

export function useUpdateQuestionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      questionId: string;
      patch: Partial<{
        promptHtml: string;
        points: number;
        payloadJson: string | null;
        modelAnswer: string | null;
      }>;
    }) => api.assessments.updateQuestion(token, input.questionId, input.patch),
    onSuccess: ({ question }) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          questions: prev.questions.map(
            (q): QuestionShape => (q.id === question.id ? { ...question, options: q.options } : q),
          ),
        };
      });
    },
  });
}

export function useDeleteQuestionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => api.assessments.deleteQuestion(token, questionId),
    onMutate: (questionId) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return { ...prev, questions: prev.questions.filter((q) => q.id !== questionId) };
      });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: assessmentTreeKey(assessmentId) });
    },
  });
}

export function useAddOptionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      questionId: string;
      label: string;
      matchValue?: string | null;
      isCorrect?: boolean;
    }) =>
      api.assessments.addOption(token, input.questionId, {
        label: input.label,
        ...(input.matchValue !== undefined && { matchValue: input.matchValue }),
        ...(input.isCorrect !== undefined && { isCorrect: input.isCorrect }),
      }),
    onSuccess: ({ option }) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          questions: prev.questions.map((q) =>
            q.id === option.questionId ? { ...q, options: [...q.options, option] } : q,
          ),
        };
      });
    },
  });
}

export function useUpdateOptionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      optionId: string;
      patch: Partial<{
        label: string;
        matchValue: string | null;
        isCorrect: boolean;
        pointsOverride: number | null;
      }>;
    }) => api.assessments.updateOption(token, input.optionId, input.patch),
    onSuccess: ({ option }) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          questions: prev.questions.map((q) =>
            q.id === option.questionId
              ? { ...q, options: q.options.map((o) => (o.id === option.id ? option : o)) }
              : q,
          ),
        };
      });
    },
  });
}

export function useDeleteOptionMutation(token: string, assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { questionId: string; optionId: string }) =>
      api.assessments.deleteOption(token, input.optionId),
    onMutate: (input) => {
      qc.setQueryData<AssessmentTreeShape>(assessmentTreeKey(assessmentId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          questions: prev.questions.map((q) =>
            q.id === input.questionId
              ? { ...q, options: q.options.filter((o) => o.id !== input.optionId) }
              : q,
          ),
        };
      });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: assessmentTreeKey(assessmentId) });
    },
  });
}
